/**
 * @graphql cc
 */
import React, { useMemo, useRef } from 'react';
import { graphql } from 'react-relay';

import { useAnalyticsEvents, type CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { CardClient } from '@atlaskit/link-provider';
import { SmartCardProvider } from '@atlaskit/link-provider';
import type { CardStore } from '@atlaskit/linking-common';

import { getCorrectContentURL } from '@atlassian/confluence-page-preview-panel';

import { expValEquals, expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useStoreOptions } from '@confluence/content-smartlinks/entry-points/useStoreOptions.ts';
import { isPageSSRd } from '@confluence/content-smartlinks/entry-points/isPageSSRd.ts';
import { getLogger } from '@confluence/logger';
import { usePageSpaceKey } from '@confluence/page-context';
import { PDF_EXPORT_PAGE } from '@confluence/named-routes/entry-points/routes.ts';
import { useIsOnRoute } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { PreviewPanelsPostMessageListener } from '@confluence/preview-panel-api/entry-points/PreviewPanelsPostMessageListener.ts';
import { useOpenPreviewPanel } from '@confluence/preview-panel-api/entry-points/useOpenPreviewPanel.ts';
import { isPreviewPanelAvailable } from '@confluence/preview-panel-api/entry-points/isPreviewPanelAvailable.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';
import { HydrationSafeComponent } from '@confluence/hydration-utils/entry-points/HydrationSafeComponent.tsx';
import {
	type FragmentReaderInterop,
	useReadFragmentInterop,
} from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop.ts';
import {
	useWriteFragmentInterop,
	type FragmentWriterInterop,
} from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop.ts';

import type { ConfluenceSmartCardProviderConsolidatedSmartCardProviderReadFragment$data } from './__generated__/ConfluenceSmartCardProviderConsolidatedSmartCardProviderReadFragment.graphql.ts';
import type { ConfluenceSmartCardProviderConsolidatedSmartCardProviderWriteFragment$data } from './__generated__/ConfluenceSmartCardProviderConsolidatedSmartCardProviderWriteFragment.graphql.ts';
import type { ConfluenceSmartCardProviderCurrentSmartCardProviderReadFragment$data } from './__generated__/ConfluenceSmartCardProviderCurrentSmartCardProviderReadFragment.graphql.ts';
import type { ConfluenceSmartCardProviderCurrentSmartCardProviderWriteFragment$data } from './__generated__/ConfluenceSmartCardProviderCurrentSmartCardProviderWriteFragment.graphql.ts';
import { ConfluenceSmartCardClient } from './ConfluenceSmartCardClient.ts';
import { liveDocSmartCardResolver } from './resolvers/liveDocSmartCardResolver.ts';

export type ConfluenceSmartCardProviderProps = Parameters<typeof SmartCardProvider>[0] & {
	doNotUsePreviewPanels?: boolean;
};

// Only log first error to console, to prevent spamming console with resolver errors
let hasLoggedResolverError = false;
const defaultConfluenceResolveHandler = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	readFragmentInterop: FragmentReaderInterop<any>,
	writeFragmentInterop: FragmentWriterInterop<any>,
	data?: unknown,
) => {
	try {
		// NOTE FOR DEVS OF RESOLVERS: The `data` object comes from linking platform's content resolver service which is an UNSTABLE API SCHEMA.
		// Ensure your resolver logic handles invalid schemas gracefully and is able to notify your team if the schema changes and your code needs to be updated.
		liveDocSmartCardResolver(createAnalyticsEvent, readFragmentInterop, writeFragmentInterop, data);
	} catch (resolverError) {
		// prevent errors from causing smart card provider issues
		if (!hasLoggedResolverError) {
			const logger = getLogger('ConfluenceSmartCardProvider');
			logger.error`resolver error: ${resolverError}`;
			hasLoggedResolverError = true;
		}
	}
};

const computeInitialStateFromCache = (smartCardClient: CardClient): CardStore | null => {
	if (expValEquals('platform_editor_smartlink_local_cache', 'isEnabled', true)) {
		// On page transition create the initial CardStore from Cache
		return !isPageSSRd() && smartCardClient instanceof ConfluenceSmartCardClient
			? smartCardClient.getInitialCardStoreFromCache()
			: null;
	} else {
		return null;
	}
};

const CurrentSmartCardProvider: typeof SmartCardProvider = (props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { children, ...restProps } = props;

	const readFragmentInterop =
		useReadFragmentInterop<ConfluenceSmartCardProviderCurrentSmartCardProviderReadFragment$data>({
			fragmentSpec: graphql`
				fragment ConfluenceSmartCardProviderCurrentSmartCardProviderReadFragment on Content {
					id
					subType
					status
					__typename
				}
			`,
		});
	const writeFragmentInterop =
		useWriteFragmentInterop<ConfluenceSmartCardProviderCurrentSmartCardProviderWriteFragment$data>({
			fragmentSpec: graphql`
				fragment ConfluenceSmartCardProviderCurrentSmartCardProviderWriteFragment on Content {
					id
					subType
					status
					__typename
				}
			`,
		});
	const smartCardClient = useMemo(
		() =>
			restProps?.client ||
			new ConfluenceSmartCardClient({
				onResolve: (data) =>
					defaultConfluenceResolveHandler(
						createAnalyticsEvent,
						readFragmentInterop,
						writeFragmentInterop,
						data,
					),
			}),
		[restProps?.client, createAnalyticsEvent, readFragmentInterop, writeFragmentInterop],
	);

	return (
		<SmartCardProvider product="CONFLUENCE" client={smartCardClient} {...restProps}>
			<HydrationSafeComponent
				isEnabled={expVal('confluence_smartlinks_suspense', 'isEnabled', false)}
			>
				{children}
			</HydrationSafeComponent>
		</SmartCardProvider>
	);
};

const ConsolidatedSmartCardProvider: typeof SmartCardProvider = (props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const readFragmentInterop =
		useReadFragmentInterop<ConfluenceSmartCardProviderConsolidatedSmartCardProviderReadFragment$data>(
			{
				fragmentSpec: graphql`
					fragment ConfluenceSmartCardProviderConsolidatedSmartCardProviderReadFragment on Content {
						id
						subType
						status
						__typename
					}
				`,
			},
		);
	const writeFragmentInterop =
		useWriteFragmentInterop<ConfluenceSmartCardProviderConsolidatedSmartCardProviderWriteFragment$data>(
			{
				fragmentSpec: graphql`
					fragment ConfluenceSmartCardProviderConsolidatedSmartCardProviderWriteFragment on Content {
						id
						subType
						status
						__typename
					}
				`,
			},
		);
	const smartCardClient = useMemo(
		() =>
			props?.client ||
			new ConfluenceSmartCardClient({
				onResolve: (data) =>
					defaultConfluenceResolveHandler(
						createAnalyticsEvent,
						readFragmentInterop,
						writeFragmentInterop,
						data,
					),
			}),
		[props?.client, createAnalyticsEvent, readFragmentInterop, writeFragmentInterop],
	);

	const initialStateFromCache = useRef<CardStore | null>(
		computeInitialStateFromCache(smartCardClient),
	);

	const { isAdminHubAIEnabled } = useSessionData();

	const { children, ...restProps } = props;

	const isPageSSR = isPageSSRd();
	const initialState = expValEquals('platform_editor_smartlink_local_cache', 'isEnabled', true)
		? (initialStateFromCache?.current ?? window['__SSR_SMARTLINKS__'])
		: window['__SSR_SMARTLINKS__'];
	const internalStoreOptions = useStoreOptions(initialState, isPageSSR);

	// Merge smartlink SSR data with any passed-in storeOptions (e.g. from shortcuts preloader)
	const presetInitialState = {
		...internalStoreOptions,
		...(props.storeOptions
			? {
					storeOptions: {
						...(expValEquals('platform_editor_smartlink_local_cache', 'isEnabled', true) === false
							? internalStoreOptions.storeOptions || {}
							: {}),
						...props.storeOptions,
						...(internalStoreOptions.storeOptions?.initialState &&
						expValEquals('platform_editor_smartlink_local_cache', 'isEnabled', true)
							? {
									initialState: {
										...internalStoreOptions.storeOptions.initialState,
										...props.storeOptions.initialState,
									},
								}
							: {}),
					},
				}
			: {}),
	};

	const shouldControlDataExport = useIsOnRoute(PDF_EXPORT_PAGE);

	return (
		<SmartCardProvider
			product="CONFLUENCE"
			client={smartCardClient}
			isAdminHubAIEnabled={isAdminHubAIEnabled}
			shouldControlDataExport={shouldControlDataExport}
			isPreviewPanelAvailable={restProps.isPreviewPanelAvailable}
			openPreviewPanel={restProps.openPreviewPanel}
			{...(expValEquals('platform_editor_smartlink_local_cache', 'isEnabled', true)
				? {}
				: presetInitialState)}
			{...restProps}
			// props.storeOptions can be set explicitly to `undefined` -- overwriting the presetInitialState storeOptions merge with props
			{...(expValEquals('platform_editor_smartlink_local_cache', 'isEnabled', true)
				? presetInitialState
				: {})}
		>
			<HydrationSafeComponent
				isEnabled={expVal('confluence_smartlinks_suspense', 'isEnabled', false)}
			>
				{children}
			</HydrationSafeComponent>
		</SmartCardProvider>
	);
};

const ConsolidatedPreviewPanelSmartCardProvider = (props: ConfluenceSmartCardProviderProps) => {
	const { open } = useOpenPreviewPanel();
	const [spaceKey] = usePageSpaceKey();
	const { cloudId } = useSessionData();
	const renderServerPlaceholder = useRenderServerPlaceholder();
	// Create a wrapper function that updates the preview panel URL
	// This is used for custom domains to update to the correct content URL and avoid CSP issues
	const openPreviewPanelWithModifiedURL = useMemo(() => {
		if (!cloudId) {
			return open;
		}
		return ({ url, ari, ...rest }: Parameters<typeof open>[0]) =>
			open({ url: getCorrectContentURL(cloudId, url, ari), ari, ...rest });
	}, [open, cloudId]);

	const { children, doNotUsePreviewPanels, ...restProps } = props;

	return isCompanyHubSpaceKey(spaceKey) || doNotUsePreviewPanels ? (
		<ConsolidatedSmartCardProvider {...restProps}>{children}</ConsolidatedSmartCardProvider>
	) : (
		<ConsolidatedSmartCardProvider
			openPreviewPanel={openPreviewPanelWithModifiedURL}
			isPreviewPanelAvailable={renderServerPlaceholder ? (_ari) => false : isPreviewPanelAvailable}
			{...restProps}
		>
			{children}
			<PreviewPanelsPostMessageListener />
		</ConsolidatedSmartCardProvider>
	);
};

export const ConfluenceSmartCardProvider = (props: ConfluenceSmartCardProviderProps) => {
	let updatedProps: ConfluenceSmartCardProviderProps = props;
	if (fg('platform_sl_3p_auth_rovo_action_kill_switch')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks -- gated, static for session lifetime
		const { isRovoEnabled, isRovoLLMEnabled } = useSessionData();
		// eslint-disable-next-line react-hooks/rules-of-hooks -- gated, static for session lifetime
		updatedProps = useMemo(
			() => ({
				...props,
				rovoOptions: {
					isRovoEnabled,
					isRovoLLMEnabled,
				},
			}),
			[props, isRovoEnabled, isRovoLLMEnabled],
		);
	}

	if (expValEquals('confluence_preview_panels_exp', 'isEnabled', true)) {
		return expValEquals('confluence_preview_panels_exp', 'isEnabled', true) ? (
			<ConsolidatedPreviewPanelSmartCardProvider {...updatedProps} />
		) : (
			<ConsolidatedSmartCardProvider {...updatedProps} />
		);
	} else {
		return <CurrentSmartCardProvider {...updatedProps} />;
	}
};
